import { useWishlist, useLocalWishlist } from '#imports'

const MAGIC_LINK_WISHLIST_KEY = 'magic-link-wishlist'
const SW_WISHLIST_KEY = 'sw-wishlist-items' // Shopware's default wishlist localStorage key

/**
 * Manages wishlist persistence for magic link login flow
 * Note: Cart merge is handled automatically by Shopware during login
 */
export function useMagicLinkCart() {
    const { items: wishlistItems, mergeWishlistProducts, canSyncWishlist } = useWishlist()
    const { clearWishlist: clearLocalWishlist } = useLocalWishlist()

    /**
     * Save current wishlist items to localStorage before sending magic link
     */
    function saveWishlistToLocal(): void {
        // Save wishlist items (cart is handled by Shopware automatically)
        if (wishlistItems.value && wishlistItems.value.length > 0) {
            localStorage.setItem(MAGIC_LINK_WISHLIST_KEY, JSON.stringify(wishlistItems.value))
        }
    }

    /**
     * Restore and merge wishlist immediately (used in callback BEFORE redirect)
     * This matches the regular login flow behavior
     * Note: Cart merge is handled automatically by Shopware during login
     */
    async function restoreAndMergeWishlist(): Promise<void> {
        const savedWishlist = localStorage.getItem(MAGIC_LINK_WISHLIST_KEY)

        if (!savedWishlist) {
            return
        }

        try {
            const savedIds: string[] = JSON.parse(savedWishlist)

            // Write to localStorage so mergeWishlistProducts can pick them up
            localStorage.setItem(SW_WISHLIST_KEY, JSON.stringify(savedIds))

            // Merge to backend if user is logged in
            if (canSyncWishlist.value) {
                await (mergeWishlistProducts() as unknown as Promise<void>)
                await clearLocalWishlist()
            }
        } catch (error) {
            console.error('Failed to restore wishlist:', error)
        } finally {
            // Clear saved backup data
            clearSavedWishlist()
        }
    }

    /**
     * Clear saved wishlist from localStorage
     */
    function clearSavedWishlist(): void {
        localStorage.removeItem(MAGIC_LINK_WISHLIST_KEY)
    }

    /**
     * Check if there's a saved wishlist in localStorage
     */
    function hasSavedWishlist(): boolean {
        return localStorage.getItem(MAGIC_LINK_WISHLIST_KEY) !== null
    }

    return {
        saveWishlistToLocal,
        restoreAndMergeWishlist,
        clearSavedWishlist,
        hasSavedWishlist
    }
}
